/**
 * Resource-pool bar color, keyed by the CONTENT resource id (#344) — the same
 * `resourceId` `resourceLabels.ts` auto-derives off a champion
 * (`ResourceDef.id`, e.g. "rage"/"energy"/"mana"). Unlike `resourceLabels.ts`
 * (#465: build-time content-glob derived), THIS table stays a small,
 * genuinely hand-kept map — color is a client-only design choice with no
 * content-side source to derive from (content has no notion of "what color is
 * this resource"), so there's nothing to auto-derive here. This is what lets
 * the SAME resource id color the bar identically everywhere it renders — the
 * above-head world-space bar (`render/entityView.ts`) AND the HUD frame bar
 * (`ui/FrameResourceBar.tsx`).
 *
 * WHY color-code at all: the HP bar is now ALWAYS green (#344 — health
 * fraction is already conveyed by bar WIDTH; a red/yellow/green fade on TOP
 * of that was redundant and, worse, made a low-resource bar look like
 * a low-health HP bar at a glance). Coloring the RESOURCE bar by pool
 * identity instead gives the WoW-familiar at-a-glance read: rage is red,
 * energy is yellow, mana is blue.
 *
 * FALLBACK: an id absent from this table (a future resource type not yet
 * colored, or `resourceId === null` — an unmapped/pool-less champion) gets
 * the light-purple PLACEHOLDER (`RESOURCE_COLOR_UNKNOWN`), never silently
 * reusing another resource's color. Mirrors `championColors.ts`'s
 * numeric-Pixi-color + `*Css` DOM-string duality.
 *
 * Sourced centrally from `theme/palette.js` (#1157).
 */

import type { ResourceId } from "@pit/protocol";
import {
  RESOURCE_COLORS,
  RESOURCE_COLOR_ENERGY,
  RESOURCE_COLOR_MANA,
  RESOURCE_COLOR_RAGE,
  RESOURCE_COLOR_UNKNOWN,
  cssHex,
} from "../theme/palette.js";

export {
  RESOURCE_COLOR_ENERGY,
  RESOURCE_COLOR_MANA,
  RESOURCE_COLOR_RAGE,
  RESOURCE_COLOR_UNKNOWN,
};

/**
 * The color for `resourceId` — its mapped color, or the light-purple
 * placeholder for `null` / an unmapped id. Always returns a usable color
 * (never null); Pixi numeric form. Pure lookup.
 */
export function resourceColor(resourceId: ResourceId | null): number {
  if (resourceId === null) return RESOURCE_COLOR_UNKNOWN;
  return RESOURCE_COLORS[resourceId] ?? RESOURCE_COLOR_UNKNOWN;
}

/**
 * `resourceColor` as a `#rrggbb` CSS string, for the DOM HUD frames
 * (`FrameResourceBar.tsx`) — Pixi takes the numeric form directly. Pure.
 */
export function resourceColorCss(resourceId: ResourceId | null): string {
  return cssHex(resourceColor(resourceId));
}
